// Billing settings & invoices API client.
// Mirrors the backend contract described in docs/billing-invoice-backend-implementation.md
// (based on docs/billing-invoice-json.md) — response field names are unchanged from the
// prior localStorage-backed placeholder, so this was a drop-in swap for the pages.

import axios from "axios";

const BASE = process.env.REACT_APP_BASE_URL;

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

// ── Billing Settings (per-project template) ────────────────────────

export const defaultBillingTemplate = () => ({
  documentTitle: "INVOICE",
  invoiceNumberPrefix: "INV-",
  dueDays: 15,
  currency: "USD",
  currencySymbol: "$",
  theme: {
    accentColor: "#2563eb",
    logoUrl: "",
  },
  from: {
    name: "",
    details: "",
  },
  to: {
    name: "",
    details: "",
  },
  billingEmails: [],
  items: [
    { description: "", type: "text", multiplier: 1 },
  ],
  fixedCharges: [
    { description: "", quantity: 1, rate: 0 },
  ],
  taxes: [
    { label: "Tax", type: "percent", value: 0 },
  ],
  discounts: [
    { label: "Discount", type: "percent", value: 0 },
  ],
  customFields: [
    { label: "Notes", value: "" },
  ],
});

export const getBillingSettings = (projectPid) =>
  axios.post(`${BASE}/api/v1/projects/billing-settings/get`, { projectPid }, { headers: authHeaders() });

export const updateBillingSettings = (projectPid, template) =>
  axios.put(`${BASE}/api/v1/projects/billing-settings/update`, { projectPid, ...template }, { headers: authHeaders() });

// ── Invoices ───────────────────────────────────────────────────────

export const defaultInvoice = (projectPid) => {
  const t = defaultBillingTemplate();
  return {
    invoicePid: null,
    projectPid,
    invoiceNumber: "",
    status: "draft", // draft | sent | paid
    documentTitle: t.documentTitle,
    currency: t.currency,
    currencySymbol: t.currencySymbol,
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: "",
    poNumber: "",
    theme: t.theme,
    from: t.from,
    to: t.to,
    items: t.fixedCharges,
    taxes: t.taxes,
    discounts: t.discounts,
    customFields: t.customFields,
    createdAt: null,
    updatedAt: null,
  };
};

export const listInvoices = (projectPid, { page = 1, limit = 20, status } = {}) => {
  const body = { projectPid, page, limit };
  if (status) body.status = status;
  return axios.post(`${BASE}/api/v1/projects/invoices/list`, body, { headers: authHeaders() });
};

// Unbilled AI usage — exactly what would land on the next invoice's usage
// line items if you created one right now (billing-settings multipliers applied).
export const getUnbilledSummary = (projectPid) =>
  axios.post(`${BASE}/api/v1/projects/invoices/unbilled-summary`, { projectPid }, { headers: authHeaders() });

// Cross-project invoice list for super admins.
export const listAllInvoices = (filters = {}) => {
  const body = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") body[key] = value;
  });
  return axios.post(`${BASE}/api/v1/projects/invoices/admin/list`, body, { headers: authHeaders() });
};

// Aggregate counts/sums for a project's sent+paid invoices (draft excluded). Replaces
// paginated list-scanning for summary widgets — accurate regardless of invoice volume.
export const getInvoiceStats = (projectPid) =>
  axios.post(`${BASE}/api/v1/projects/invoices/stat`, { projectPid }, { headers: authHeaders() });

export const getInvoice = (invoicePid) =>
  axios.post(`${BASE}/api/v1/projects/invoices/get`, { invoicePid }, { headers: authHeaders() });

export const createInvoice = (payload) =>
  axios.post(`${BASE}/api/v1/projects/invoices/create`, payload, { headers: authHeaders() });

export const updateInvoice = (payload) =>
  axios.put(`${BASE}/api/v1/projects/invoices/update`, payload, { headers: authHeaders() });

export const deleteInvoice = (invoicePid) =>
  axios.delete(`${BASE}/api/v1/projects/invoices/delete`, { headers: authHeaders(), data: { invoicePid } });

export const sendInvoice = (invoicePid, toEmail) =>
  axios.post(`${BASE}/api/v1/projects/invoices/${invoicePid}/send`, { toEmail }, { headers: authHeaders() });

// Idempotent — safe to call on an already-paid invoice. Returns the full invoice object.
export const markInvoicePaid = (invoicePid) =>
  axios.post(`${BASE}/api/v1/projects/invoices/${invoicePid}/mark-paid`, {}, { headers: authHeaders() });

// Records a (partial or full) payment. Returns the full invoice object with updated
// status/paidAmount/dueAmount/isPaid.
export const recordPayment = (invoicePid, payload) =>
  axios.post(`${BASE}/api/v1/projects/invoices/${invoicePid}/payments`, payload, { headers: authHeaders() });

// Oldest → newest payment history for an invoice.
export const listPayments = (invoicePid) =>
  axios.get(`${BASE}/api/v1/projects/invoices/${invoicePid}/payments`, { headers: authHeaders() });

// Downloads the server-rendered PDF and triggers a browser save-as.
export const downloadInvoicePdf = async (invoicePid, filename = "invoice.pdf") => {
  const res = await axios.get(`${BASE}/api/v1/projects/invoices/${invoicePid}/pdf`, {
    headers: authHeaders(),
    responseType: "blob",
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

// Opens an invoice's already-stored PDF (invoice.pdfUrl, e.g. "/api/v1/uploads/media/:mediaPid")
// inline in a new tab. Same generic auth-protected media route used for logos/documents.
export const viewInvoicePdf = async (pdfUrl) => {
  const res = await axios.get(`${BASE}${pdfUrl}`, {
    headers: authHeaders(),
    responseType: "blob",
  });
  const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(blobUrl, "_blank");
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
};

// ── Derived totals (client-side live preview; server also returns its own on save) ──

export const computeTotals = (invoice) => {
  const subtotal = (invoice.items || []).reduce(
    (sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.rate) || 0),
    0
  );
  const lineTotal = (row) =>
    row.type === "flat" ? Number(row.value) || 0 : (subtotal * (Number(row.value) || 0)) / 100;

  const taxLines = (invoice.taxes || []).map((t) => ({ ...t, amount: lineTotal(t) }));
  const discountLines = (invoice.discounts || []).map((d) => ({ ...d, amount: lineTotal(d) }));

  const tax = taxLines.reduce((sum, t) => sum + t.amount, 0);
  const discount = discountLines.reduce((sum, d) => sum + d.amount, 0);
  const total = Math.max(0, subtotal + tax - discount);
  return { subtotal, tax, discount, total, taxLines, discountLines };
};

export const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "INR", symbol: "₹" },
  { code: "AUD", symbol: "A$" },
  { code: "CAD", symbol: "C$" },
  { code: "JPY", symbol: "¥" },
  { code: "AED", symbol: "د.إ" },
  { code: "SGD", symbol: "S$" },
];
